import { useMemo } from 'react';

import { GetDetailedProductQuery, GetProductPriceRangesQuery, GetProductPricesQuery } from 'data/__generated__';

import { AuditLogsSection } from 'components/AuditLogs/AuditLogsSection';
import type { SubjectLabels } from 'components/AuditLogs';

/**
 * L'historique d'un produit — ses prix, ses gammes de produits et son groupe compris.
 *
 * Un produit se modifie par morceaux qui vivent chacun dans leur collection : changer un prix n'écrit
 * RIEN sur le document produit, tout part sur `priceRangeProducts`. Sans les identifiants de ces
 * documents, la section resterait vide sur l'édition la plus courante de la page.
 *
 * Le serveur ne connaît aucune hiérarchie, et c'est voulu : ce que le produit englobe, c'est cette page
 * qui le sait — elle a déjà tout à l'écran. Ce qu'une même requête a touché ailleurs arrive tout seul,
 * par le `requestId` porté par chaque ligne d'audit.
 */
export const ProductAuditSection = ({
    product,
    productPrices,
    productPriceRanges,
}: {
    product: NonNullable<GetDetailedProductQuery['detailedProduct']>;
    productPrices: GetProductPricesQuery['productPrices'];
    productPriceRanges: GetProductPriceRangesQuery['productPriceRanges'];
}) => {
    const { collectionDocumentIds, subjectLabels } = useMemo(() => {
        const documentIds = [product._id];
        const labels: SubjectLabels = {};

        product.productArrangements.forEach(({ _id, name }) => {
            documentIds.push(_id);
            labels[_id] = `« ${name} »`;
        });

        if (product.productGroup) {
            documentIds.push(product.productGroup._id);
        }

        productPrices.forEach(({ priceRangeProductId, rangeId }) => {
            // Nul quand le prix est hérité d'une gamme de produits : aucun document ne le porte.
            if (!priceRangeProductId) {
                return;
            }

            documentIds.push(priceRangeProductId);
            labels[priceRangeProductId] =
                `« ${productPriceRanges.find(({ _id }) => _id === rangeId)?.name ?? rangeId} »`;
        });

        return { collectionDocumentIds: documentIds, subjectLabels: labels };
    }, [product, productPrices, productPriceRanges]);

    return <AuditLogsSection collectionDocumentIds={collectionDocumentIds} subjectLabels={subjectLabels} />;
};
